/** Baseline demo values when a slot has no live tag/value. Numbers re-jitter every minute via useDTDemoTick. */

const DEMO_INTERVAL_MS = 60 * 1000;

function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function strHash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(31, h) + s.charCodeAt(i) | 0;
  return Math.abs(h);
}

/** @typedef {Record<string, number|string>} DemoMap */

export const DT_DEMO_BASES = {
  'dt-voltage-table': { L1: 415, L2: 418, L3: 408, Average: 414 },
  'dt-current-table': {
    RMS_R: 1208,
    RMS_Y: 1210,
    RMS_B: 1214,
    THD_R: 3,
    THD_Y: 4,
    THD_B: 5,
  },
  'dt-radial-gauge': { contactWearR: 2, contactWearY: 4, contactWearB: 7 },
  'dt-metering-bars': {
    ir: 1208,
    iy: 1210,
    ib: 1214,
    in: 2,
    vry: 418,
    vyb: 415,
    vbr: 412,
  },
  'dt-temperature-grid': {
    hbbR: 72,
    hbbY: 68,
    hbbB: 70,
    ctR: 45,
    ctY: 48,
    ctB: 46,
  },
  'dt-temperature-trend': { temp1: 65, temp2: 67, temp3: 63 },
  'dt-vfd-status': {
    vfdStatus: 1,
    motorVoltage: 415,
    dcLinkVoltage: 565,
    current: 1205,
    power: 85,
    frequency: 49.9,
    powerFactor: 0.94,
  },
  'dt-status-indicators': {
    thermalStatus: 0,
    tripInfo: 0,
    status: 1,
    L1: 415,
    L2: 418,
    L3: 408,
    Average: 414,
    RMS_R: 1208,
    RMS_Y: 1210,
    RMS_B: 1214,
    THD_R: 3,
    THD_Y: 4,
    THD_B: 5,
  },
  'dt-general-info': { iframe: 1200, irated: 1600, poles: 4 },
  'dt-breaker-data-extras': {
    faultCounterL: 12,
    faultCounterI: 8,
    faultCounterG: 3,
    faultCounterS: 1,
    onOperation: 2450,
    offOperation: 2410,
  },
};

function spreadForKey(key) {
  if (key.startsWith('contactWear')) return { spread: 0.8, decimals: 1 };
  if (key.startsWith('THD_')) return { spread: 1.2, decimals: 1 };
  if (key.startsWith('RMS_') || key === 'ir' || key === 'iy' || key === 'ib') return { spread: 18, decimals: 0 };
  if (key === 'in') return { spread: 2, decimals: 1 };
  if (key === 'poles' || key.startsWith('faultCounter') || key.endsWith('Operation')) return { spread: 2, decimals: 0 };
  if (key === 'vfdStatus' || key === 'thermalStatus' || key === 'tripInfo' || key === 'status') return { spread: 0, decimals: 0 };
  if (key === 'powerFactor') return { spread: 0.04, decimals: 2 };
  if (key === 'frequency') return { spread: 0.8, decimals: 1 };
  if (key === 'power' || key === 'current' || key === 'iframe' || key === 'irated') return { spread: 15, decimals: 0 };
  if (key.startsWith('temp') || key.startsWith('hbb') || key.startsWith('ct')) return { spread: 3, decimals: 1 };
  if (key.startsWith('v') && key.length === 3) return { spread: 6, decimals: 0 };
  if (key === 'motorVoltage' || key === 'dcLinkVoltage') return { spread: 8, decimals: 0 };
  if (key === 'L1' || key === 'L2' || key === 'L3' || key === 'Average') return { spread: 5, decimals: 0 };
  return { spread: 4, decimals: 0 };
}

/**
 * @param {string} widgetType
 * @param {number} tick increments every DEMO_INTERVAL_MS
 * @returns {DemoMap}
 */
export function buildJitteredDemoMap(widgetType, tick) {
  const bases = DT_DEMO_BASES[widgetType];
  if (!bases) return {};
  const rnd = mulberry32((tick * 1103515245 + strHash(widgetType)) >>> 0);
  /** @type {DemoMap} */
  const out = {};
  Object.entries(bases).forEach(([key, base]) => {
    if (typeof base !== 'number') {
      out[key] = base;
      return;
    }
    const { spread, decimals } = spreadForKey(key);
    if (spread === 0) {
      out[key] = base;
      return;
    }
    const delta = (rnd() - 0.5) * 2 * spread;
    const v = base + delta;
    if (decimals === 0) out[key] = Math.round(v);
    else {
      const f = 10 ** decimals;
      out[key] = Math.round(v * f) / f;
    }
  });
  return out;
}

/** True when this slot is bound in tagprops and live payload has a usable value. */
export function hasLiveSlotValue(resolved) {
  if (!resolved) return false;
  const v = resolved.value;
  if (v === null || v === undefined) return false;
  if (typeof v === 'string' && v.trim() === '') return false;
  return true;
}

export { DEMO_INTERVAL_MS };
